import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Divider } from "@/components/Divider";
import { SectionHeader } from "@/components/SectionHeader";
import { CheckCircle2 } from "lucide-react";

const programs = [
  {
    id: "conventional",
    name: "Conventional Loans",
    tagline: "The flexible workhorse of the mortgage world.",
    desc: "Available for primary residences, second homes, and investment properties. Down payments start at 3% for qualified first-time buyers and 5% for everyone else.",
    bullets: [
      "Down payments from 3% (first-time) / 5% standard",
      "Drop PMI at 20% equity",
      "Fixed and adjustable rate options",
      "Loan limits to county conforming caps",
    ],
  },
  {
    id: "fha",
    name: "FHA Loans",
    tagline: "Designed to open the door to homeownership.",
    desc: "Government-backed loans with relaxed credit guidelines and a low 3.5% down payment requirement. Ideal for first-time buyers or those rebuilding credit.",
    bullets: [
      "3.5% minimum down payment",
      "Credit scores from 580",
      "Gift funds permitted",
      "Streamline refinance available",
    ],
  },
  {
    id: "va",
    name: "VA Loans",
    tagline: "Earned benefits for those who served.",
    desc: "Backed by the Department of Veterans Affairs. Zero down payment, no monthly mortgage insurance, and some of the most competitive rates available.",
    bullets: [
      "Zero down payment",
      "No monthly PMI",
      "Available to veterans, active duty, Guard, Reserve",
      "Limited closing costs",
    ],
  },
  {
    id: "usda",
    name: "USDA Loans",
    tagline: "Zero-down financing for rural America.",
    desc: "Designed to encourage homeownership in eligible rural and suburban areas. 100% financing for borrowers who meet income and location requirements.",
    bullets: [
      "Zero down payment",
      "Eligible rural & suburban areas",
      "Income limits apply",
      "Competitive fixed rates",
    ],
  },
  {
    id: "jumbo",
    name: "Jumbo Loans",
    tagline: "Financing beyond conforming limits.",
    desc: "For higher-value homes that exceed Fannie Mae / Freddie Mac conforming limits. Stricter underwriting, but competitive rates for well-qualified borrowers.",
    bullets: [
      "Loan amounts above conforming limits",
      "10–20% down typical",
      "Strong credit and reserves required",
      "Fixed and ARM options",
    ],
  },
  {
    id: "refinance",
    name: "Refinance",
    tagline: "Restructure for what life looks like now.",
    desc: "Rate-and-term refinances to lower your payment, cash-out refinances to fund renovations or debt consolidation, and streamline programs for FHA/VA.",
    bullets: [
      "Rate-and-term refinance",
      "Cash-out refinance",
      "FHA & VA streamline options",
      "Shorten or extend your term",
    ],
  },
];

export default function LoanPrograms() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div data-testid="page-loan-programs">
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651] mb-4">Loan Programs</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-black tracking-tight max-w-4xl">
            Six programs. One originator who knows them cold.
          </h1>
          <Divider className="my-6" />
          <p className="font-sub italic text-xl text-gray-600 max-w-3xl">
            Whether you&apos;re buying your first home, your forever home, or your next
            investment property, there&apos;s a program designed for you.
          </p>
        </div>
      </section>

      <div className="brand-divider-full" />

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {programs.map((p, i) => {
            const colors = ["#00A651", "#E88C2F", "#ED1C24", "#F26522", "#00A651", "#E88C2F"];
            const c = colors[i % colors.length];
            return (
              <article
                key={p.id}
                id={p.id}
                data-testid={`program-${p.id}`}
                className="grid lg:grid-cols-12 gap-8 bg-white border border-gray-200 p-6 sm:p-10 rounded-sm border-l-4 scroll-mt-32"
                style={{ borderLeftColor: c }}
              >
                <div className="lg:col-span-3">
                  <div className="font-heading font-bold text-5xl" style={{ color: c }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-black mt-3">
                    {p.name}
                  </h2>
                  <p className="font-sub italic text-gray-600 mt-2">{p.tagline}</p>
                </div>
                <div className="lg:col-span-6">
                  <p className="text-gray-700 leading-relaxed">{p.desc}</p>
                  <ul className="mt-5 space-y-3">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 style={{ color: c }} className="mt-0.5" size={18} />
                        <span className="text-gray-700">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-3 flex flex-col gap-3 lg:items-end justify-center">
                  <Link
                    to={`/apply?program=${p.id}`}
                    data-testid={`apply-${p.id}`}
                    className="text-white font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm transition-opacity hover:opacity-90 w-full lg:w-auto text-center"
                    style={{ backgroundColor: c }}
                  >
                    Apply for {p.name.split(' ')[0]}
                  </Link>
                  <Link
                    to="/contact"
                    className="text-xs uppercase tracking-wider font-bold text-black hover:text-[#F26522] transition-colors"
                  >
                    Ask a Question →
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
